<script lang="ts" setup>
import { ref, onMounted, reactive } from "vue";
import { Globals } from "@/Globals";
import CommonVCardDialog from "@/components/common/VCardDialog.vue";
import TagesSaldenAntrag from "@/views/SoftTerm/TagesSaldenAntrag.vue";
import TagesSaldenTimer from "@/views/SoftTerm/TagesSaldenTimer.vue";
import TagesSaldenBelege from "@/views/SoftTerm/TagesSaldenBelege.vue";
import TagesSaldenBestaetigungen from "@/views/SoftTerm/TagesSaldenBestaetigungen.vue";
import { getKennungValueDisplay, getKennungTitle } from "@/DataHelper";
import { globalsBezeichnung, checkBegriff } from "@/local";
import { getSiteData } from "@/NavigationHelper";
import AUApi from "@/api";
import { mountedBase, onSave, BaseSetup } from "@/DataLoaderStatic";
import { goBack } from "@/NavigationHelper";

const emit = defineEmits<{
  back: [];
  calculated: [data?: any];
}>();
let soll = ref<string>("");
let ist = ref<string>("");
let saldo = ref<string>("");
let differenz = ref<string>("");
let pause = ref<string>("");
let verfallBeginn = ref<string>("");
let verfallEnde = ref<string>("");
let sperreAntrag = ref<boolean>(false);
let showBestaetigung = ref<boolean>(false);
let isVorgesetzter = ref<boolean>(false);
let showBelege = ref<boolean>(false);

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "PZE_Web_TagesSalden",
  emitBack: () => emit("back"),
};
let zeitbereich = pageParams.zeitbereich;
let aic = pageParams.aic;
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

onMounted(async () => {
  const siteData = getSiteData();
  if (siteData) {
    if (siteData.zeitbereich) {
      zeitbereich = siteData.zeitbereich;
    }
    if (siteData.aic) {
      aic = siteData.aic;
    }

    if (
      (siteData.sperre === "1.00" && !siteData.isVorgesetzter) ||
      (siteData.sperre && !siteData.isVorgesetzter) // beim VG darf NICHT gesperrt werden
    ) {
      // wird nun mir TRUE übergeben!
      sperreAntrag.value = true;
    }
  }
  if (siteData && siteData.isVorgesetzter) {
    isVorgesetzter.value = true;
    if (checkBegriff("Web_VG_ErfassungBelege")) {
      showBestaetigung.value = true;
    }
  } else {
    if (checkBegriff("Web_Bestaetigungen")) {
      showBestaetigung.value = true;
    }
    if (checkBegriff("RK_Web_ALL_sonstigeBelege")) {
      showBelege.value = true;
    }
  }
  await mountedBase(uiData, pageParams);
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    soll.value = getKennungValueDisplay(row, "Soll", uiData.columns);
    ist.value = getKennungValueDisplay(row, "Ist", uiData.columns);
    pause.value = getKennungValueDisplay(row, "Pause", uiData.columns);
    saldo.value = getKennungValueDisplay(row, "Saldo", uiData.columns);
    differenz.value = getKennungValueDisplay(row, "Differenz", uiData.columns);
    verfallBeginn.value = getKennungValueDisplay(
      row,
      "VerfallzeitBeginn",
      uiData.columns,
    );
    verfallEnde.value = getKennungValueDisplay(
      row,
      "VerfallzeitEnde",
      uiData.columns,
    );
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
</script>
<template>
  <v-sheet max-width="100%" v-if="uiData.initialized" class="pb-2">
    <v-card :loading="uiData.showLoading" class="ma-2 pa-2" variant="text">
      <v-card-text class="pa-0 salden-grid">
        <span class="salden-label">{{
          getKennungTitle("Soll", uiData.columns)
        }}</span>
        <span class="salden-value">{{ soll }}</span>
        <span class="salden-label salden-col3">{{
          getKennungTitle("Pause", uiData.columns)
        }}</span>
        <span class="salden-value">{{ pause }}</span>

        <span class="salden-label">{{
          getKennungTitle("Ist", uiData.columns)
        }}</span>
        <span class="salden-value">{{ ist }}</span>
        <span class="salden-label salden-col3">{{
          getKennungTitle("VerfallzeitBeginn", uiData.columns)
        }}</span>
        <span class="salden-value">{{ verfallBeginn }}</span>

        <span class="salden-label">{{
          getKennungTitle("Differenz", uiData.columns)
        }}</span>
        <span class="salden-value">{{ differenz }}</span>
        <span class="salden-label salden-col3">{{
          getKennungTitle("VerfallzeitEnde", uiData.columns)
        }}</span>
        <span class="salden-value">{{ verfallEnde }}</span>

        <span class="salden-label">{{
          getKennungTitle("Saldo", uiData.columns)
        }}</span>
        <span class="salden-value">{{ saldo }}</span>
        <span class="salden-label salden-col3"></span>
        <span class="salden-value"></span>
      </v-card-text>
    </v-card>

    <CommonVCardDialog
      textQuery="ZE_Gesamtsaldo_Neu"
      :text="
        globalsBezeichnung(
          Globals.Begriff,
          'Web_Gesamtsaldo',
          'akt. Gesamtsaldo',
        )
      "
      textKennung="BERECHNUNG_MASS"
      :zeitbereich="zeitbereich"
      :aic="aic"
    ></CommonVCardDialog>

    <TagesSaldenAntrag
      v-if="checkBegriff('Web_SW_Detailantrag') && !isVorgesetzter"
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="pa-0 pt-2"
      :sperre="sperreAntrag"
    ></TagesSaldenAntrag>

    <TagesSaldenTimer
      v-if="isVorgesetzter"
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="px-0 pt-2"
    ></TagesSaldenTimer>

    <TagesSaldenBestaetigungen
      v-if="showBestaetigung"
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="pa-0 pt-2"
    ></TagesSaldenBestaetigungen>

    <TagesSaldenBelege
      v-if="showBelege"
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="pa-0 pt-2"
    ></TagesSaldenBelege>
  </v-sheet>
</template>
<style scoped>
.salden-grid {
  display: grid;
  grid-template-columns: max-content 1fr max-content 1fr;
  column-gap: 8px;
  row-gap: 4px;
  align-items: baseline;
  width: 100%;
}
.salden-col3 {
  /* Abstand zur linken Werte-Spalte, damit die 2 Gruppen optisch getrennt bleiben */
  padding-left: 16px;
}
.salden-label {
  white-space: nowrap;
  opacity: 0.7;
}
.salden-value {
  white-space: nowrap;
  text-align: right;
  font-weight: 500;
}
@media (max-width: 550px) {
  .v-card__subtitle,
  .v-card__text,
  .v-card__title {
    padding: 10px;
  }
}
</style>
